import { useEffect, useMemo } from "react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { MetricGrid } from "@/components/ui/MetricGrid";
import { FeatureCard } from "@/components/ui/FeatureCard";
import { UseCaseList } from "@/components/ui/UseCaseList";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ContactForm } from "@/components/ui/contact/contactForm";
import { Safari } from "@/components/ui/safari";

const heroImageUrl = "/assets/gyoai-hero.png";
const logoGyoaiUrl = "/assets/logo-gyoai.svg";
const gmailIconUrl = "/assets/gmail.png";
const googleCalendarIconUrl = "/assets/googlecalendar.png";

type BusinessReportAiPageProps = {
  onNavigate?: (path: string) => void;
};

type MetaSnapshot = {
  el: HTMLElement;
  attr: "content" | "href";
  prev: string | null;
  created: boolean;
};

function upsertMetaByName(name: string, content: string): MetaSnapshot {
  const selector = `meta[name="${name}"]`;
  const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
  const el = existing ?? document.createElement("meta");
  const created = existing == null;
  if (created) el.setAttribute("name", name);
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  if (created) document.head.appendChild(el);
  return { el, attr: "content", prev, created };
}

function upsertMetaByProperty(property: string, content: string): MetaSnapshot {
  const selector = `meta[property="${property}"]`;
  const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
  const el = existing ?? document.createElement("meta");
  const created = existing == null;
  if (created) el.setAttribute("property", property);
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  if (created) document.head.appendChild(el);
  return { el, attr: "content", prev, created };
}

function upsertLinkCanonical(href: string): MetaSnapshot {
  const selector = `link[rel="canonical"]`;
  const existing = document.head.querySelector(selector) as HTMLLinkElement | null;
  const el = existing ?? document.createElement("link");
  const created = existing == null;
  if (created) el.setAttribute("rel", "canonical");
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  if (created) document.head.appendChild(el);
  return { el, attr: "href", prev, created };
}

function restoreMeta(snaps: MetaSnapshot[]) {
  for (const s of snaps) {
    if (s.created) {
      s.el.remove();
      continue;
    }
    if (s.prev == null) s.el.removeAttribute(s.attr);
    else s.el.setAttribute(s.attr, s.prev);
  }
}

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function BusinessReportAiPage({ onNavigate }: BusinessReportAiPageProps) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "業務報告AI｜メールに返信するだけで業務報告書が完成 | モノバンドル株式会社";

    const origin = typeof location !== "undefined" && location.origin ? location.origin : "";
    const canonical = origin ? `${origin}/service/gyoai` : "/service/gyoai";

    const description =
      "業務報告AIは、商談が終わるとヒアリングメールを自動送信し、担当者がそれに返信するだけで業務報告書を自動作成するサービスです。Google Calendar と Gmail を連携し、報告の抜け漏れ防止・品質の標準化・共有の効率化をまとめて実現します。";

    const ogTitle = "業務報告AI｜メールに返信するだけで業務報告書が完成";

    const snaps: MetaSnapshot[] = [];
    snaps.push(upsertMetaByName("description", description));
    snaps.push(upsertMetaByProperty("og:title", ogTitle));
    snaps.push(upsertMetaByProperty("og:description", description));
    snaps.push(upsertMetaByName("twitter:title", ogTitle));
    snaps.push(upsertMetaByName("twitter:description", description));
    snaps.push(upsertLinkCanonical(canonical));

    return () => {
      document.title = prevTitle;
      restoreMeta(snaps);
    };
  }, []);

  const go = (path: string) => {
    if (onNavigate) onNavigate(path);
    else if (typeof window !== "undefined") {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  const tldrCopy =
    "メールに返信するだけで業務報告書が完成する";

  const metrics = useMemo(
    () => [
      {
        metric: "入力ゼロ",
        label: "メール返信だけで完了",
        detail:
          "商談後に届くヒアリングメールに返信するだけ。専用画面への入力や手書き報告書の作成は不要です。",
      },
      {
        metric: "抜け漏れゼロ",
        label: "商談を自動で検出",
        detail:
          "Google Calendar から商談を自動抽出。報告対象の取りこぼしを仕組みで防止します。",
      },
      {
        metric: "品質を標準化",
        label: "AIが報告書を整形",
        detail:
          "ヒアリング項目と整形プロンプトにより、誰が返信しても同じ粒度・フォーマットの報告書に仕上がります。",
      },
    ],
    [],
  );

  const features = useMemo(
    () => [
      {
        title: "商談の自動検出（Google Calendar 連携）",
        description:
          "Google Calendar のイベントから商談を自動抽出。報告対象の取りこぼしを仕組みで防ぎます。キーワード・色・参加者ドメインでの絞り込みにも対応。",
      },
      {
        title: "ヒアリング依頼メールの自動送信（Gmail 連携）",
        description:
          "商談終了後、担当者へヒアリング項目を含むメールを自動送信。BANT・MEDDICC・独自項目など、テンプレートは会社ごとに設定できます。",
      },
      {
        title: "メール返信の自動回収・原文メモ化",
        description:
          "担当者がメールに返信するだけで、スレッド内の返信を自動回収。原文メモとしてそのまま蓄積し、転記作業を不要にします。",
      },
      {
        title: "AIによる報告書の自動整形",
        description:
          "回収した返信内容をAIが読みやすい報告書に自動整形。見出し・項目・トーンは会社単位でカスタマイズ可能です。",
      },
      {
        title: "ダッシュボードで共有・検索・コピー",
        description:
          "メンバー別・日付別での絞り込み、原文メモ・整形済み報告書のワンクリックコピーに対応。SFA/CRMへの転記もスムーズです。",
      },
      {
        title: "マルチテナント・認証・課金",
        description:
          "会社（テナント）単位で設定を分離。JWT＋Cookie 認証、Stripe の per-seat 課金に対応し、チーム規模に応じてスケールします。",
      },
    ],
    [],
  );

  const howItWorks = useMemo(
    () => [
      "管理者がメンバーを追加（管理画面から招待）",
      "担当者が招待リンクから Google 連携（本人が OAuth で認証）",
      "Google Calendar の予定変更を自動検知し、商談を同期",
      "商談終了後、ヒアリング項目を含むメールを担当者へ自動送信",
      "担当者がメールに返信（ここだけが人の作業）",
      "返信を自動回収し、AIが報告書を整形・生成",
      "ダッシュボードで報告書を共有・検索・コピー",
    ],
    [],
  );

  const useCases = useMemo(
    () => [
      {
        title: "営業報告の遅延・抜け漏れをなくしたい",
        body: "商談後にメールが届くため、報告忘れが起きません。マネージャーはダッシュボードで「どの案件が未報告か」をリアルタイムに把握できます。",
      },
      {
        title: "報告書のフォーマット・品質を統一したい",
        body: "ヒアリング項目と整形プロンプトを会社単位で管理。BANT・MEDDICC・独自項目に合わせて、誰が返信しても同じ粒度の報告書に仕上がります。",
      },
      {
        title: "商談ナレッジを蓄積・共有したい",
        body: "返信原文（生のヒアリング内容）と整形済み報告書の両方を保存。引き継ぎ・他部署への共有・過去案件の振り返りに活用できます。",
      },
    ],
    [],
  );

  const faq = useMemo(
    () => [
      {
        q: "本当にメールに返信するだけで報告書ができるのですか？",
        a: "はい。商談終了後にヒアリング項目を含むメールが届くので、担当者はそのメールに返信するだけです。返信内容はAIが自動的に読みやすい報告書に整形し、ダッシュボードに公開します。専用画面へのログインや手入力は不要です。",
      },
      {
        q: "どんなメールが届きますか？",
        a: "商談終了後、あらかじめ設定したヒアリング項目（例: BANT、MEDDICC、独自項目など）を含むメールが Gmail で届きます。質問に沿って返信するだけで、報告に必要な情報が漏れなく回収されます。",
      },
      {
        q: "どの Google アカウントが連携されますか？",
        a: "連携対象の担当者ご本人が招待リンクから認証を行い、その方の Google（Calendar / Gmail）に接続します。ユーザーごとに連携情報を保持するため、社内で複数人が同時に運用可能です。管理者が個人のパスワードを預かる必要はありません。",
      },
      {
        q: "ヒアリング依頼メールはいつ送られますか？",
        a: "商談（カレンダー予定）の終了後、一定時間が経過したタイミングで自動送信します（初期設定は「終了1分後」）。送信までの時間や実行頻度は運用に合わせて調整可能です。「当日中に回収したい」「翌営業日にまとめて送りたい」といった運用にも対応できます。",
      },
      {
        q: "返信が複数ある場合はどうなりますか？",
        a: "スレッド内に複数の返信がある場合でも、返信本文をまとめて回収し原文メモとして整理します。そのうえでAIが読みやすい報告書に自動整形します。後から追加の返信があった場合も、追記として取り込まれます。",
      },
      {
        q: "報告書の形式（見出し・項目・トーン）は変えられますか？",
        a: "はい。会社（テナント）単位で、ヒアリング項目や報告書の見出し構成、トーン（丁寧さ / 要点重視など）を調整できます。「上長が欲しい情報を先に出したい」「CRM項目に合わせたい」といった要望にも対応できます。",
      },
      {
        q: "既存の SFA / CRM と併用できますか？",
        a: "はい。ダッシュボードから整形済み報告書をワンクリックでコピーし、お使いの SFA / CRM に貼り付けられます。報告書の項目構成を CRM の入力項目に合わせることも可能です。",
      },
    ],
    [],
  );

  const structuredData = useMemo(() => {
    const appJsonLd = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "業務報告AI",
      alternateName: "メールに返信するだけで業務報告書が完成するサービス",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description:
        "業務報告AIは、商談終了後にヒアリングメールを自動送信し、担当者がメールに返信するだけで業務報告書を自動作成するクラウドサービスです。Google Calendar と Gmail を連携し、報告の抜け漏れ防止・品質の標準化・共有の効率化を実現します。",
      provider: {
        "@type": "Organization",
        name: "モノバンドル株式会社",
        url: "https://monobundle.com",
      },
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "JPY",
        description: "資料請求・導入相談は無料。詳細はお問い合わせください。",
      },
      featureList: [
        "メールに返信するだけで業務報告書が完成",
        "Google Calendar から商談を自動検出・同期",
        "商談終了後のヒアリング依頼メールを Gmail で自動送信",
        "メール返信を自動回収し原文メモとして保存",
        "AIによる報告書の自動整形（見出し・項目・トーンをカスタマイズ可能）",
        "ダッシュボードで報告書を共有・検索・コピー",
        "会社（テナント）単位の設定・認証・Stripe per-seat 課金",
      ],
    };

    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    };

    return { appJsonLd, faqJsonLd };
  }, [faq]);

  return (
    <article itemScope itemType="https://schema.org/SoftwareApplication">
      <meta itemProp="name" content="業務報告AI" />
      <meta itemProp="applicationCategory" content="BusinessApplication" />
      <meta itemProp="operatingSystem" content="Web" />

      {/* Hero */}
      <section className="overflow-hidden border-b border-border/60 py-16">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-[minmax(0,420px)_1fr] md:gap-10 lg:gap-14">
            <div className="min-w-0">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Business Automation
              </p>
              <div className="mt-4">
                <img
                  src={logoGyoaiUrl}
                  alt="業務報告AI"
                  className="h-10 w-auto md:h-12"
                />
              </div>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                2分。メールに返信、
                <br className="hidden sm:inline" />
                業務報告書が完成。
              </h1>
              <p className="mt-6 max-w-2xl text-muted-foreground leading-relaxed">
                商談の検出・ヒアリング依頼の送信・返信の回収・報告書の整形・ダッシュボードでの共有まですべて自動化します。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <img
                    src={googleCalendarIconUrl}
                    alt="Google Calendar"
                    className="h-6 w-6 shrink-0 rounded object-contain"
                  />
                  Google Calendar
                </span>
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <img
                    src={gmailIconUrl}
                    alt="Gmail"
                    className="h-6 w-6 shrink-0 rounded object-contain"
                  />
                  Gmail
                </span>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button size="lg" className="min-w-[180px]" onClick={() => scrollToId("contact")}>
                  資料請求・デモ相談（無料）
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="min-w-[180px]"
                  onClick={() => scrollToId("faq")}
                >
                  よくある質問を見る
                </Button>
              </div>
            </div>

            <div className="relative -mr-6 md:-mr-64 lg:-mr-[28rem] xl:-mr-[36rem]">
              <div className="absolute -inset-6 -z-10 rounded-[28px] bg-gradient-to-b from-muted/50 via-muted/10 to-transparent blur-2xl" />
              <Safari
                url="monobundle.com"
                imageSrc={heroImageUrl}
                aspectRatio="2632/1434"
                mediaFit="contain"
                mediaPosition="top left"
                className="w-full rounded-xl shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_20px_70px_-40px_rgba(0,0,0,0.6)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 要約 */}
      <section className="border-b border-border/60 px-6 py-16 md:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="rounded-lg border border-border/60 bg-muted/10 p-6">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
              TL;DR
            </p>
            <p className="mt-5 text-lg font-medium leading-relaxed md:text-xl">
              {tldrCopy}
            </p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              業務報告AIは、Google Calendar で商談を自動検出し、終了後にヒアリングメールを Gmail で自動送信します。
              担当者はそのメールに返信するだけ。返信内容をAIが読みやすい報告書に整形し、ダッシュボードで共有できます。
              SFA やCRM への転記作業も、コピー＆ペーストで効率化できます。
            </p>
          </div>
        </div>
      </section>

      {/* Why now */}
      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            eyebrow="Why"
            title="なぜ「メール返信だけ」で報告書が完成するのか"
            description={
              <>
                商談後の報告は、忙しいほど後回しになりがちです。
                業務報告AIは「カレンダー予定」と「メール返信」だけを起点にすることで、
                報告の記録・整形・共有を現場に負担をかけずに自動化します。
              </>
            }
          />
          <div className="mt-10">
            <MetricGrid items={metrics} />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border/60 bg-muted/20 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            eyebrow="Features"
            title="業務報告AIの主な機能"
            description={
              <>
                Google Calendar による商談検出から、Gmail でのヒアリング依頼・返信回収、
                AIによる報告書の自動整形、ダッシュボードでの共有まで。
                担当者はメールに返信するだけで、報告プロセス全体が自動で完了します。
              </>
            }
          />

          <div className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border/60 bg-border/60 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <FeatureCard key={f.title} title={f.title} description={f.description} />
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Button size="lg" className="min-w-[180px]" onClick={() => scrollToId("contact")}>
              導入相談をする
            </Button>
            <Button variant="ghost" size="lg" className="min-w-[180px]" onClick={() => scrollToId("how")}>
              仕組みを見る
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            eyebrow="How it works"
            title="メール返信だけで報告書ができるまでの流れ"
            description="新しいツールを覚える必要はありません。いつものカレンダーとメール運用に自然に組み込まれます。"
          />

          <div className="relative mt-10">
            <div
              className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-border via-border to-transparent"
              aria-hidden
            />
            <ol className="space-y-8">
              {howItWorks.map((title, i) => (
                <li key={title} className="relative flex gap-6 pl-8 sm:pl-10">
                  <span
                    className="absolute left-3 top-[0.35rem] h-2 w-2 -translate-x-1/2 shrink-0 rounded-full border border-border bg-background shadow-sm"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1 pb-1">
                    <p className="text-[15px] leading-snug text-foreground/90">
                      {i + 1}. {title}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="border-t border-border/60 bg-muted/20 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            eyebrow="Use cases"
            title="こんな課題を解決します"
            description="営業報告の遅延・抜け漏れ・品質のばらつきを、現場とマネジメントの両方から改善します。"
          />
          <UseCaseList items={useCases} className="mt-12" />
        </div>
      </section>

      {/* Pricing + Security */}
      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-14">
            <div>
              <SectionHeader
                eyebrow="Pricing"
                title="小さく試して、人数に応じてスケール"
                description={
                  <>
                    Free から開始し、運用人数に応じて Premium へ。課金は per-seat（1ユーザーあたり月額）です。
                  </>
                }
              />
              <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-border/60 bg-border/60 sm:grid-cols-2">
                {[
                  {
                    title: "Free",
                    body: "連携対象ユーザー 1人まで",
                  },
                  {
                    title: "Premium",
                    body: "980円/ユーザー/月",
                  },
                ].map((p) => (
                  <div key={p.title} className="bg-card px-6 py-8">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                      {p.title}
                    </p>
                    <p className="mt-3 text-sm text-foreground/90 leading-relaxed">{p.body}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                ※価格・プランは運用形態に合わせて調整可能です。詳細はお問い合わせください。
              </p>
            </div>

            <div>
              <SectionHeader
                eyebrow="Security"
                title="企業向けの基本要件を満たす設計"
                description="テナント分離と認証を前提に、外部企業向けSaaS運用に必要な要点を押さえています。"
              />
              <ul className="mt-8 space-y-4 text-sm text-foreground/90">
                {[
                  "認証: JWT を httpOnly Cookie で管理（本番では Secure 付与）",
                  "テナント分離: すべての管理APIは companyId でスコープ",
                  "Webhook: Stripe Webhook は署名検証を実装",
                  "Cron: 本番で CRON_SECRET により保護可能",
                  "AI: 整形対象は返信本文（原文メモ）。会社ごとに出力の粒度や形式を調整可能",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/30" aria-hidden />
                    <span className="leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <Button variant="outline" size="lg" className="min-w-[180px]" onClick={() => scrollToId("contact")}>
                  セキュリティ要件を相談する
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-border/60 bg-muted/20 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            eyebrow="FAQ"
            title="よくある質問"
            description="導入・運用・カスタマイズの疑問にお答えします。"
          />
          <div className="mt-10 space-y-3">
            {faq.map((item) => (
              <details
                key={item.q}
                className="group rounded-lg border border-border/60 bg-background/50 px-5 py-4 open:bg-background"
              >
                <summary className="cursor-pointer list-none text-sm font-medium text-foreground/90">
                  {item.q}
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 業務報告AIとは — LLM/検索エンジン向け定義セクション */}
      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            eyebrow="About"
            title="業務報告AIとは"
            description="業務報告AIの仕組みと主要な概念を説明します。"
          />

          <div className="mt-10 rounded-lg border border-border/60 bg-muted/10 p-6">
            <p className="text-sm text-foreground/90 leading-relaxed">
              <span className="font-medium">業務報告AI</span>は、商談終了後にヒアリングメールを自動送信し、
              担当者がそのメールに返信するだけで業務報告書を自動作成するクラウドサービスです。
              Google Calendar と Gmail を連携し、商談の検出・ヒアリング依頼の送信・返信の回収・AIによる報告書の整形・ダッシュボードでの共有までを一気通貫で自動化します。
              モノバンドル株式会社が開発・提供しています。
            </p>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {[
              {
                term: "商談（Deal）",
                def: "Google Calendar のイベントから自動抽出される商談レコード。キーワード・色・参加者ドメインで抽出条件を設定できます。",
              },
              {
                term: "ヒアリングメール",
                def: "商談終了後に担当者へ自動送信されるメール。あらかじめ設定したヒアリング項目（BANT・MEDDICC等）が含まれます。",
              },
              {
                term: "原文メモ",
                def: "担当者のメール返信を自動回収し、そのまま保存した元データ。加工前の一次情報として残ります。",
              },
              {
                term: "整形済み報告書",
                def: "原文メモをAIが読みやすく整理した共有用の報告書。見出し・項目・トーンは会社ごとにカスタマイズ可能です。",
              },
            ].map((g) => (
              <div key={g.term} className="rounded-lg border border-border/60 bg-muted/10 px-6 py-5">
                <p className="text-sm font-medium text-foreground">{g.term}</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{g.def}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t border-border/60 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Contact
              </p>
              <h2 className="mt-3 font-serif text-2xl font-light tracking-tight text-foreground md:text-3xl">
                資料請求・デモ相談（無料）
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground leading-relaxed">
                「どのイベントを商談にするか」「ヒアリング項目テンプレ」「報告書フォーマット」「セキュリティ要件」など、貴社の運用に合わせてご提案します。
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => go("/service")}>
                サービス一覧へ
              </Button>
              <Button variant="ghost" onClick={() => scrollToId("how")}>
                仕組みを見る
              </Button>
            </div>
          </div>

          <Card className="overflow-hidden border-border/50 bg-card/50 shadow-none transition-colors hover:bg-card/80">
            <CardHeader className="space-y-2 px-5 pb-3 sm:space-y-3 sm:px-8 sm:pb-4">
              <p className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                資料請求
              </p>
              <h3 className="font-serif text-xl font-light leading-snug tracking-tight text-foreground sm:text-2xl">
                まずは現状の運用を教えてください
              </h3>
              <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
                「メールに返信するだけで報告書が完成する」状態を最短で実現するための、導入パターンと運用設計をご案内します。
              </p>
            </CardHeader>
            <CardContent className="px-5 pb-8 sm:px-8">
              <ContactForm />
              <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
                送信後、担当よりご連絡します。内容に応じてオンラインでのヒアリングをお願いする場合があります。
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData.appJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData.faqJsonLd) }}
      />
    </article>
  );
}

